import { Injectable } from '@angular/core';
import type { Params } from '@angular/router';
import { Auth } from '@freelancer/auth';
import type {
  UserEmailRequestAjax,
  UserEmailRequestGetResultAjax,
} from '@freelancer/datastore/collections';
import { _transformUserStatus } from '@freelancer/datastore/collections';
import type {
  BackendErrorResponse,
  BackendSuccessResponse,
  ResponseData,
} from '@freelancer/freelancer-http';
import { FreelancerHttp } from '@freelancer/freelancer-http';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import type { BackendErrorTypes } from 'api-typings/error-distribution';
import { ErrorCodeApi } from 'api-typings/errors/errors';
import type { UserApi } from 'api-typings/users/users';
import { firstValueFrom } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import type { OnVerifyEmailPostResultAjaxApi } from './email-verification.backend-model';
import type {
  EmailChangeRequestGetResult,
  EmailChangeRequestUpdateResult,
} from './email-verification.model';
import { transformEmailChangeRequest } from './email-verification.transformers';

interface EmailVerifiedResponse {
  readonly status: 'success';
  readonly isEmailVerified: boolean;
}

type EmailVerificationResponse =
  | EmailVerifiedResponse
  | BackendErrorResponse<
      BackendErrorTypes['user_get_self'] | ErrorCodeApi.NOT_FOUND
    >;

type OnVerifyEmailError =
  | ErrorCodeApi.INTERNAL_SERVER_ERROR
  | ErrorCodeApi.METHOD_NOT_ALLOWED
  | ErrorCodeApi.BAD_REQUEST
  | ErrorCodeApi.USER_ACCOUNT_CLOSED_OR_SUSPENDED
  | ErrorCodeApi.EMAIL_VERIFICATION_CODE_MISMATCH
  | ErrorCodeApi.TOO_MANY_REQUESTS;

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class EmailVerification {
  constructor(private auth: Auth, private freelancerHttp: FreelancerHttp) {}

  /**
   * Sends a verification email
   * or returns an error response if any error occurred or
   * if the user is already verified.
   *
   * The `redirect_url` is where to redirect the user
   * after clicking the verification email link.
   */
  send(
    redirectUrl?: string,
  ): Promise<
    | BackendSuccessResponse
    | BackendErrorResponse<
        | BackendErrorTypes['user_action_email_verify']
        | ErrorCodeApi.USER_ACCOUNT_CLOSED_OR_SUSPENDED
        | 'UNKNOWN_ERROR'
      >
  > {
    return firstValueFrom(
      this.auth.isLoggedIn().pipe(
        switchMap(loggedIn => {
          if (!loggedIn) {
            throw new Error('No user to send verification email to!');
          }
          return this.freelancerHttp.put<
            | BackendSuccessResponse
            | BackendErrorResponse<
                BackendErrorTypes['user_action_email_verify']
              >
          >('users/0.1/self?action=email_verify', {
            redirect_url: redirectUrl,
          });
        }),
        untilDestroyed(this),
      ),
    );
  }

  check(): Promise<EmailVerificationResponse> {
    return firstValueFrom(
      this.auth.isLoggedIn().pipe(
        switchMap(isloggedIn => {
          if (!isloggedIn) {
            throw new Error(
              'User must be logged in to check email verification',
            );
          }
          return this.freelancerHttp.get<UserApi>('users/0.1/self?status=true');
        }),
        untilDestroyed(this),
      ),
    ).then(response => {
      if (response.status !== 'success') {
        return response;
      }
      if (response.result.status === undefined) {
        return {
          status: 'error',
          errorCode: ErrorCodeApi.NOT_FOUND,
          requestId: response.requestId,
        } as EmailVerificationResponse;
      }
      const status = _transformUserStatus(response.result.status);
      return {
        status: 'success',
        isEmailVerified: status.emailVerified,
      };
    });
  }

  /**
   * Submits a request to change user's email.
   *
   * This involves creating an email change request and
   * sending a verification email to the user.
   */
  changeEmail(
    email: string,
    password?: string,
  ): Promise<EmailChangeRequestUpdateResult> {
    const formData = new FormData();
    formData.append('email', email);

    if (password) {
      formData.append('password', password);
    }

    return firstValueFrom(
      this.freelancerHttp
        .post<UserEmailRequestAjax>('user/settings/email.php', formData, {
          isGaf: true,
        })
        .pipe(untilDestroyed(this)),
    ).then(response => {
      if (response.status !== 'success') {
        return response;
      }

      return {
        status: 'success',
        emailChangeRequest: transformEmailChangeRequest(response.result),
      };
    });
  }

  /**
   * Gets all the pending email change requests of the user.
   */
  getEmailChangeRequest(): Promise<EmailChangeRequestGetResult> {
    return firstValueFrom(
      this.freelancerHttp
        .get<UserEmailRequestGetResultAjax>('user/settings/email.php', {
          isGaf: true,
        })
        .pipe(untilDestroyed(this)),
    ).then(response => {
      if (response.status !== 'success') {
        return response;
      }

      const changeRequests = response.result.email_requests.map(request =>
        transformEmailChangeRequest(request),
      );

      return {
        status: 'success',
        emailChangeRequests: changeRequests,
        allowPasswordlessChange: response.result.allow_passwordless_change,
      };
    });
  }

  verifyToken(
    queryParams: Params = {},
  ): Promise<ResponseData<OnVerifyEmailPostResultAjaxApi, OnVerifyEmailError>> {
    const { verifyCode, userId, projectId, goto } = queryParams;
    const endpoint = 'email/onVerifyEmail.php';
    return firstValueFrom(
      this.freelancerHttp
        .post<OnVerifyEmailPostResultAjaxApi, OnVerifyEmailError>(
          endpoint,
          {
            verifyCode,
            userId,
            projectId,
            goto,
          },
          {
            isGaf: true,
            serializeBody: true,
          },
        )
        .pipe(untilDestroyed(this)),
    );
  }
}
